<template>
  <div id="insider-backtester-general-tab">
    <v-container fluid pa-0>
      <v-row>
        <!-- Date range -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="d-inline-flex align-center">
            <v-chip
              v-if="!accessAllowed({ requires_pro_subscription: true })"
              color="accent text-white"
              class="v-chip--x-small font-weight-bold ma-0 mr-2"
            >
              PRO
            </v-chip>
            Date range:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-menu
                :close-on-content-click="false"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                transition="scale-transition"
                min-width="290px"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-model="criteria.start_date"
                    :disabled="!accessAllowed({ requires_pro_subscription: true })"
                    label="Start date"
                    :prepend-icon="mdiCalendar"
                    hide-details
                    readonly
                    v-bind="props"
                  />
                </template>
                <v-date-picker v-model="criteria.start_date" no-title scrollable />
              </v-menu>
            </v-col>

            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-menu
                :close-on-content-click="false"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                transition="scale-transition"
                min-width="290px"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-model="criteria.end_date"
                    :disabled="!accessAllowed({ requires_pro_subscription: true })"
                    placeholder=" "
                    label="End date (optional)"
                    :prepend-icon="mdiCalendar"
                    hide-details
                    readonly
                    v-bind="props"
                  />
                </template>
                <v-date-picker v-model="criteria.end_date" no-title scrollable />
              </v-menu>
            </v-col>
          </v-row>
        </v-col>

        <!-- Starting amount -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>Starting amount:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.starting_amount"
                type="number"
                prefix="$"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Trading Delay -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>Trading delay:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.days_after_acceptance_date"
                type="number"
                suffix="days"
                hint="Days after an insider filing is released before it is considered for backtesting"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Limit new purchase of a previously held stock -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header
            >Limit new purchase of a previously held stock:</helpers-section-sub-header
          >
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.limit_new_purchases_days"
                type="number"
                suffix="days"
                hint="Number of days elapsed before backtest can repurchase previously held stock"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- New Buy as % of Portfolio -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>New buy as % of portfolio:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.portfolio_weighting_percent_entry"
                type="number"
                suffix="%"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Hold Stock Minimum # of days -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>Hold stock minimum # of days:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="criteria.minimum_holding_days"
                type="number"
                suffix="days"
                hint="Stock cannot be sold for at least # of days"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- How to manage portfolio when fully-invested -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            How to manage portfolio when fully-invested:
          </helpers-section-sub-header>
          <v-radio-group v-model="fullyInvestedFilter" hide-details>
            <v-radio :value="0" label="Buy using all remaining cash (min 1 share)" />
            <v-radio :value="1" label="First in First Out (FIFO) Sell to acquire funds" />
            <v-radio :value="2" label="Sell underperforming holdings first to acquire funds" />
          </v-radio-group>
        </v-col>

        <!-- Technical Price Filters -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Technical price filters
          </helpers-section-sub-header>
          <p>
            Discover insiders who are buying when a stock declines/advances above or below your
            specified moving average
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-text-field
                v-model="criteria.stock_price_ma"
                type="number"
                suffix="days"
                label="Moving average # days"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-text-field
                v-model="criteria.stock_price_ma_below"
                :disabled="!criteria.stock_price_ma"
                type="number"
                suffix="%"
                label="% below moving average"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-text-field
                v-model="criteria.stock_price_ma_above"
                :disabled="!criteria.stock_price_ma"
                type="number"
                suffix="%"
                label="% above moving average"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'

// Mixins
import AccessMixin from '@/mixins/access'
import { mdiCalendar } from '@mdi/js'

export default {
  mixins: [AccessMixin],

  data() {
    return {
      fullyInvestedFilter: 1,
      mdiCalendar
    }
  },

  computed: {
    ...mapFields({
      criteria: 'app.insiderBacktesterCriteria'
    })
  },

  watch: {
    fullyInvestedFilter(val) {
      if (val === 0) {
        this.criteria.fully_invested_buy_up_to_cash_available = true
        this.criteria.fully_invested_exit_fifo = false
        this.criteria.fully_invested_exit_underperform = false
      }
      if (val === 1) {
        this.criteria.fully_invested_buy_up_to_cash_available = false
        this.criteria.fully_invested_exit_fifo = true
        this.criteria.fully_invested_exit_underperform = false
      }
      if (val === 2) {
        this.criteria.fully_invested_buy_up_to_cash_available = false
        this.criteria.fully_invested_exit_fifo = false
        this.criteria.fully_invested_exit_underperform = true
      }
    }
  }
}
</script>
